// FS-2026-065: one explicit declaration for each legal party being registered.
import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCurrentPlatformPolicy } from "src/api/platform-legal";
import { useTranslate } from "src/locales";
import type { LegalRegistrationState } from "src/types/platform-legal";
import LegalDocumentText from "src/components/legal-document-text/legal-document-text";

export const EMPTY_LEGAL_REGISTRATION: LegalRegistrationState = {
  valid: false,
  provider_legal_registrations: [],
};
type Identity = {
  legal_name: string;
  legal_address: string;
  tax_number: string;
  confirmed?: string;
};
const emptyIdentity: Identity = {
  legal_name: "",
  legal_address: "",
  tax_number: "",
};
export default function LegalRegistrationFields({
  providerTypes = [],
  includePlatform = false,
  minor = false,
  onChange,
}: {
  providerTypes?: string[];
  includePlatform?: boolean;
  minor?: boolean;
  onChange: (value: LegalRegistrationState) => void;
}) {
  const { t } = useTranslate();
  const current = useCurrentPlatformPolicy();
  const release = current.data?.release;
  const [identities, setIdentities] = useState<Record<string, Identity>>({});
  const [confirmedTerms, setTerms] = useState("");
  const [confirmedPrivacy, setPrivacy] = useState("");
  const [confirmedGuardian, setGuardian] = useState("");
  const key = release
    ? `${release.id}:${release.checksum}:${release.locale}`
    : "";
  const reported = useRef("");
  const typesKey = JSON.stringify(Array.from(new Set(providerTypes)));
  useEffect(() => {
    const report = (value: LegalRegistrationState) => {
      const serialized = JSON.stringify(value);
      if (reported.current !== serialized) {
        reported.current = serialized;
        onChange(value);
      }
    };
    if (!current.data || current.error || current.isLoading) {
      report(EMPTY_LEGAL_REGISTRATION);
      return;
    }
    if (!release) {
      report({ valid: true, provider_legal_registrations: [] });
      return;
    }
    const types: string[] = JSON.parse(typesKey);
    const providers = types.map((provider_type) => {
      const item = identities[provider_type] || emptyIdentity;
      return {
        provider_type,
        release_id: release.id,
        agreement_checksum: release.documents.provider_agreement.checksum,
        locale: release.locale,
        legal_name: item.legal_name.trim(),
        legal_address: item.legal_address.trim(),
        tax_number: item.tax_number.trim(),
        representative_confirmed: item.confirmed === key,
      };
    });
    const platformValid =
      !includePlatform ||
      (confirmedTerms === key &&
        confirmedPrivacy === key &&
        (!minor || confirmedGuardian === key));
    report({
      valid:
        platformValid &&
        providers.every(
          (item) =>
            item.representative_confirmed &&
            item.legal_name.length >= 2 &&
            item.legal_address.length >= 5 &&
            item.tax_number.length >= 5,
        ),
      provider_legal_registrations: providers,
      ...(includePlatform
        ? {
            platform_acceptance: {
              release_id: release.id,
              checksum: release.checksum,
              locale: release.locale,
              terms_accepted: confirmedTerms === key,
              privacy_acknowledged: confirmedPrivacy === key,
              capacity: minor ? "guardian" : "self",
            },
          }
        : {}),
    });
  }, [
    current.data,
    current.error,
    current.isLoading,
    release,
    typesKey,
    identities,
    key,
    includePlatform,
    minor,
    confirmedTerms,
    confirmedPrivacy,
    confirmedGuardian,
    onChange,
  ]);
  if (current.error)
    return (
      <Alert
        severity="error"
        action={
          <Button
            onClick={() => {
              void current.mutate();
            }}
          >
            {t("retry")}
          </Button>
        }
      >
        {t("platform_legal_error")}
      </Alert>
    );
  if (!current.data || current.isLoading) return <CircularProgress size={24} />;
  if (!release) return null;
  const document = (kind: "terms" | "privacy" | "provider_agreement") => (
    <Accordion key={kind}>
      <AccordionSummary>{release.documents[kind].title}</AccordionSummary>
      <AccordionDetails>
        <LegalDocumentText>{release.documents[kind].body}</LegalDocumentText>
      </AccordionDetails>
    </Accordion>
  );
  return (
    <Stack spacing={2} sx={{ my: 2 }}>
      {includePlatform && (
        <>
          {document("terms")}
          {document("privacy")}
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmedTerms === key}
                onChange={(_, value) => setTerms(value ? key : "")}
              />
            }
            label={t("platform_legal_terms_confirm")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmedPrivacy === key}
                onChange={(_, value) => setPrivacy(value ? key : "")}
              />
            }
            label={t("platform_legal_privacy_confirm")}
          />
          {minor && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmedGuardian === key}
                  onChange={(_, value) => setGuardian(value ? key : "")}
                />
              }
              label={t("platform_legal_guardian_confirm")}
            />
          )}
        </>
      )}
      {providerTypes.length > 0 && (
        <>
          <Typography variant="h6">{t("provider_legal_title")}</Typography>
          <Alert severity="info">{t("provider_legal_notice")}</Alert>
          {document("provider_agreement")}
        </>
      )}
      {Array.from(new Set(providerTypes)).map((type) => {
        const item = identities[type] || emptyIdentity;
        return (
          <Stack key={type} spacing={2}>
            <Typography variant="subtitle1">{t(type)}</Typography>
            {(["legal_name", "legal_address", "tax_number"] as const).map(
              (field) => (
                <TextField
                  key={field}
                  InputLabelProps={{ required: true }}
                  label={t(
                    {
                      legal_name: "provider_legal_name",
                      legal_address: "provider_legal_address",
                      tax_number: "provider_legal_taxnumber",
                    }[field],
                  )}
                  value={item[field]}
                  inputProps={{
                    maxLength:
                      field === "legal_address"
                        ? 500
                        : field === "legal_name"
                        ? 200
                        : 50,
                  }}
                  onChange={(event) =>
                    setIdentities((old) => ({
                      ...old,
                      [type]: {
                        ...(old[type] || emptyIdentity),
                        [field]: event.target.value,
                        confirmed: undefined,
                      },
                    }))
                  }
                />
              ),
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={item.confirmed === key}
                  onChange={(_, value) =>
                    setIdentities((old) => ({
                      ...old,
                      [type]: {
                        ...(old[type] || emptyIdentity),
                        confirmed: value ? key : undefined,
                      },
                    }))
                  }
                />
              }
              label={t("provider_legal_confirm")}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}
